import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StorageLocation } from '@azko/shared';

import { ApiRequestError, api } from '../lib/api.js';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  LoadingRows,
  PageHeader,
  StatusBadge,
  TextInput,
} from '../components/ui.js';

/**
 * Onde a encomenda fica guardada.
 *
 * O campo era livre: o porteiro digitava "Armario A - 03" dezenas de vezes por
 * dia, de celular, com a caixa na outra mao. A base acumulava "armario a 03",
 * "ARM A3" e "Arm. A - 3" como se fossem lugares diferentes, e procurar onde o
 * pacote esta nao achava nenhum.
 *
 * O campo livre NAO morreu — quem nao cadastra nada aqui continua digitando lá,
 * e quem cadastra ainda tem a opcao "outro" para o caso nao previsto. Esta tela
 * so preenche a lista de escolhas.
 */

function LocationRow({ item }: { item: StorageLocation }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: (patch: { name?: string; isActive?: boolean }) =>
      api.patch<void>(`/armarios/${item.id}`, patch),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['storage-locations'] });
    },
    onError: (mutationError) => {
      setError(
        mutationError instanceof ApiRequestError
          ? mutationError.message
          : 'Nao foi possivel salvar.',
      );
    },
  });

  return (
    <li className="px-4 py-3 sm:px-6 sm:py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {editing ? (
          <form
            className="flex min-w-0 flex-1 flex-wrap items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              update.mutate({ name: name.trim() });
            }}
          >
            <TextInput
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={80}
              autoFocus
              className="min-w-0 flex-1 sm:max-w-xs"
              aria-label="Nome do lugar"
            />
            <Button type="submit" loading={update.isPending} className="py-2">
              Salvar
            </Button>
            <Button
              type="button"
              variant="quiet"
              className="py-2"
              onClick={() => {
                setName(item.name);
                setEditing(false);
                setError(null);
              }}
            >
              Cancelar
            </Button>
          </form>
        ) : (
          <>
            <div className="min-w-0">
              <p className="truncate font-bold">{item.name}</p>
              <p className="mt-0.5 text-sm text-muted">
                {item.packages === 0
                  ? 'Nenhuma encomenda aqui agora'
                  : `${item.packages} encomenda${item.packages > 1 ? 's' : ''} aguardando retirada`}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {!item.isActive && <StatusBadge tone="neutral">Desativado</StatusBadge>}
              <Button variant="quiet" className="py-1.5" onClick={() => setEditing(true)}>
                Renomear
              </Button>
              <Button
                variant="quiet"
                className="py-1.5"
                loading={update.isPending}
                onClick={() => update.mutate({ isActive: !item.isActive })}
              >
                {item.isActive ? 'Desativar' : 'Reativar'}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Desativar com pacote dentro nao quebra nada, e some do select — e do
          radar de quem ainda precisa entregar esses pacotes. */}
      {item.isActive && item.packages > 0 && (
        <p className="mt-2 text-sm text-faint">
          Desativar tira do formulario de entrada. As {item.packages} encomendas continuam
          registradas aqui.
        </p>
      )}

      {error && (
        <p className="mt-2 text-sm text-danger" role="alert">
          {error}
        </p>
      )}
    </li>
  );
}

export function StoragePage() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['storage-locations', 'todos'],
    // Inclui os desativados: e daqui que eles voltam.
    queryFn: () => api.get<{ items: StorageLocation[] }>('/armarios?inativos=true'),
  });

  const create = useMutation({
    mutationFn: () => api.post<StorageLocation>('/armarios', { name: name.trim() }),
    onSuccess: () => {
      setName('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['storage-locations'] });
    },
    onError: (mutationError) => {
      setError(
        mutationError instanceof ApiRequestError
          ? mutationError.message
          : 'Nao foi possivel cadastrar.',
      );
    },
  });

  const items = data?.items ?? [];

  return (
    <>
      <PageHeader title="Armarios" subtitle="Onde a portaria guarda encomenda — para escolher em vez de digitar." />

      <Card>
        <CardHeader
          title="Novo lugar"
          subtitle="Use o nome que a portaria ja usa: Armario A, Prateleira grandes, Sala 2."
        />

        <form
          className="flex flex-wrap items-end gap-3 px-5 pb-5 sm:px-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <Field label="Nome" className="min-w-0 flex-1 sm:max-w-sm">
            <TextInput
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Armario A"
              maxLength={80}
            />
          </Field>
          <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
            Cadastrar
          </Button>
        </form>

        {error && (
          <div className="px-5 pb-5 sm:px-6">
            <Alert>{error}</Alert>
          </div>
        )}
      </Card>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <LoadingRows rows={3} />
        ) : items.length === 0 ? (
          <EmptyState
            title="Nenhum lugar cadastrado"
            description="Sem cadastro, a portaria continua digitando o local a cada entrada — que funciona, mas e onde nascem os cinco nomes para o mesmo armario."
          />
        ) : (
          <ul className="divide-y divide-line-soft">
            {items.map((item) => (
              <LocationRow key={item.id} item={item} />
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
